import { useMutation, useQuery } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';

import Icon from 'shopper/components/Icon';

import useModal from 'hooks/useModal';
import useQueryClient from 'hooks/useQueryClient';
import useToast from 'hooks/useToast';

import { stripObjDuplicates } from 'lib/array';
import { sendEvent } from 'lib/gtag';
import {
  getFromLocalStorage,
  removeFromLocalStorage,
  setLocalStorage,
} from 'lib/localStorage';
import placeholder from 'lib/placeholder';
import { offerQueryKeys } from 'lib/queryKeys';
import { isUrlValid } from 'lib/string';
import { sendSupixEvent } from 'lib/supix';
import { noop, scrollToTop } from 'lib/utils';

import { useUser } from 'providers/UserProvider';

import { getOfferValidationInfo, postNewOffer } from 'services/offer';

import { useStepper } from '../Stepper/StepperProvider';
import OfferLinkForm from './OfferLinkForm';

const LoadingOfferInfo = dynamic(() => import('./LoadingOfferInfo'), {
  ssr: false,
});

const MakeSureGoBackModal = dynamic(() => import('./MakeSureGoBackModal'), {
  ssr: false,
});

const OfferInfoForm = dynamic(() => import('./OfferInfoForm'), { ssr: false });

const DAILY_LIMIT_REACHED_ERROR_CODE = 'DAILY_LIMIT_EXCEEDED';
const RECOVERY_FORM_DATA_LOCAL_STORAGE_KEY = 'offer.form-recovery';
const MIN_LOADING_TIME = 3 * 1000; // 3s

const SuggestNewOfferForm = ({
  gaEventCategory,
  onSubmitSuccess = noop,
  ...rest
}) => {
  const { isLoggedIn, runCallbackIfLoggedIn } = useUser();
  const { showModal } = useModal();
  const { changeActiveStep } = useStepper();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [isTimeoutEnabled, setTimeoutEnabled] = useState(false);
  const [offerUrl, setOfferUrl] = useState('');
  const [isShowingFirstFormStep, setShowingFirstFormStep] = useState(true);
  const [isTimeoutRan, setTimeoutRan] = useState(false);

  const {
    data: offerInfo = null,
    isInitialLoading,
    isError,
  } = useQuery({
    queryKey: offerQueryKeys.validation({ offerUrl }),
    enabled: offerUrl.length > 0,
    cacheTime: 0,
    staleTime: Infinity,
    queryFn: ({ signal }) => {
      setTimeoutEnabled(true);

      return isUrlValid(offerUrl)
        ? getOfferValidationInfo({ offerUrl }, { signal })
        : null;
    },
    onSettled: () => {
      sendEvent({
        action: 'url_sent',
        category: gaEventCategory,
      });
    },
    onError: () => {
      sendEvent({
        action: 'crawler_error',
        category: gaEventCategory,
      });
    },
    select: (data) =>
      data
        ? {
            ...data,
            offerImages: data.offerImages
              ? stripObjDuplicates(data.offerImages, 'offerPhoto')
              : [],
          }
        : null,
    onSuccess: (data) => {
      if (data.offerTitle) {
        changeActiveStep({
          type: 'next',
        });
      }

      if (data.price) {
        changeActiveStep({
          type: 'next',
        });
      }

      const crawlerHasSuccess =
        !!data.offerTitle || data.offerImages.length > 0;

      sendEvent({
        action: crawlerHasSuccess ? 'crawler_success' : 'crawler_error',
        category: gaEventCategory,
      });
    },
  });

  const { mutate: mutateSuggestNewOffer, reset: resetSuggestNewOfferMutation } =
    useMutation({
      mutationFn: postNewOffer,
      onSuccess: (data) => {
        setTimeoutRan(false);
        resetSuggestNewOfferMutation();
        onSubmitSuccess(data);
        sendSupixEvent('/ofs', {
          oid: data.offerId,
        });
      },
      onMutate: () => {
        sendEvent({
          action: 'submit_attempt',
          category: gaEventCategory,
        });
      },
      onError: ({ code }) => {
        const message =
          code === DAILY_LIMIT_REACHED_ERROR_CODE
            ? placeholder('toasts.errors.offers.suggestionDailyLimit')
            : placeholder('toasts.errors.unknown');

        showToast({
          iconLeft: <Icon name="error-circle" />,
          text: message,
          type: 'danger',
        });
      },
    });

  useEffect(() => {
    const json = getFromLocalStorage(RECOVERY_FORM_DATA_LOCAL_STORAGE_KEY);

    if (json && isLoggedIn) {
      const data = JSON.parse(json);

      onSubmit(data, { persistFormData: false });
      removeFromLocalStorage(RECOVERY_FORM_DATA_LOCAL_STORAGE_KEY);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    return () => {
      if (offerUrl) {
        queryClient.removeQueries(offerQueryKeys.validation({ offerUrl }));
      }
    };
  }, [offerUrl, queryClient]);

  useEffect(() => {
    let timeout;

    if (isTimeoutEnabled) {
      timeout = setTimeout(() => {
        setTimeoutRan(true);
      }, MIN_LOADING_TIME);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [isTimeoutEnabled]);

  const onCleanup = useCallback(() => {
    setTimeoutRan(false);
    setTimeoutEnabled(false);
  }, []);

  const onNextClick = useCallback(({ offerUrl: url }) => {
    const urlAsURLObj = new URL(url);

    setOfferUrl(
      `${urlAsURLObj.origin}${urlAsURLObj.pathname}${encodeURIComponent(
        urlAsURLObj.search
      )}`
    );
    changeActiveStep({ type: 'next' });
    setShowingFirstFormStep(false);
  }, []);

  const onGoBackClick = useCallback(() => {
    sendEvent({
      category: gaEventCategory,
      action: 'button_back_click',
    });

    setShowingFirstFormStep(false);
    showModal(MakeSureGoBackModal, {
      gaEventCategory,
      onExitClick: () => {
        changeActiveStep({ type: 'reset' });
        changeActiveStep({ type: 'next' });
        scrollToTop();
        setShowingFirstFormStep(true);
      },
    });
  }, []);

  const onSubmit = useCallback(
    (data, { persistFormData } = { persistFormData: true }) => {
      if (!isLoggedIn) {
        sendEvent({
          action: 'submit_unauth',
          category: gaEventCategory,
        });

        if (persistFormData) {
          setLocalStorage(
            RECOVERY_FORM_DATA_LOCAL_STORAGE_KEY,
            JSON.stringify(data)
          );
        }
      }

      runCallbackIfLoggedIn(() => {
        mutateSuggestNewOffer(data);
      });
    },
    [isLoggedIn, runCallbackIfLoggedIn]
  );

  const isShowingLoadingState = isTimeoutRan ? isInitialLoading : true;

  return (
    <div {...rest}>
      {isShowingFirstFormStep && (
        <OfferLinkForm
          gaEventCategory={gaEventCategory}
          offerUrl={offerUrl}
          onNextClick={onNextClick}
        />
      )}
      {!isShowingFirstFormStep &&
        (isShowingLoadingState ? (
          <LoadingOfferInfo gaEventCategory={gaEventCategory} />
        ) : (
          <OfferInfoForm
            gaEventCategory={gaEventCategory}
            isError={isError}
            offerInfo={offerInfo}
            offerUrl={offerUrl}
            onCleanup={onCleanup}
            onGoBackClick={onGoBackClick}
            onSubmit={onSubmit}
          />
        ))}
    </div>
  );
};

SuggestNewOfferForm.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
  onSubmitSuccess: PropTypes.func,
};

export default SuggestNewOfferForm;
